/*
ORGANIZATION-FROM
Setup params form the whole organization (if not defined cannot search establishment)
*/
import Card from "@/components/card"
import PageHeader from "@/components/page-header"
import TextInput from "@/components/text-input"
import { t } from "i18next"
import { store, useAppDispatch, useAppSelector } from "@/core/root-reducer"
import Space from "@/components/space"
import Button from "@/components/button"
import {
  faCity,
  // faInfoCircle,
  faSave,
  faTrash
} from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import organizationStore from "../../organization/model/organization.store"
import { createEmptyOrganization } from "../model/organization.types"
import OrganizationDeleteModal from "./modals/OrganizationDeleteModal"
// import Checkbox from "@/components/checkbox"
// import Tooltip from "@/components/tooltip"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { toast } from "react-toastify"
import establishmentStore from "../../establishment/model/establishment.store"
import { sessionEdit } from "@/redux/_session.actions"
import { RISKS_INTERFACE_TYPE } from "@/redux/_session.types"
import { Establishment } from "../../establishment/model/establishment.types"

export default function OrganizationForm() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const status = useAppSelector(organizationStore.selectors.status)
  const organizationExists = useAppSelector(organizationStore.selectors.exists)
  const draftOrganization = useAppSelector(organizationStore.selectors.draft)
  const _session = useAppSelector((state) => state._session)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const updateField =
    <K extends keyof typeof draftOrganization>(key: K) =>
    ({ value }: { value: (typeof draftOrganization)[K] }) => {
      dispatch(organizationStore.actions.edit({ key, value }))
      setIsDirty(true)
    }

  // const setValue = <K extends keyof typeof draftOrganization>(key: K, value: (typeof draftOrganization)[K]) => {
  //   dispatch(organizationStore.actions.edit({ key, value }))
  //   setIsDirty(true)
  // }

  const handleDelete = () => {
    if (!draftOrganization?.id) return
    setIsDeleteModalOpen(true)
  }

  const handleSave = async () => {
    if (!draftOrganization.name) {
      toast(t("Merci de remplir le nom de l'organisation"))
      return
    }
    const organization = await dispatch(organizationStore.thunks.updateEntity()).unwrap()
    dispatch(organizationStore.actions.setActive(organization))
    if (!organization.isMultiple) {
      const establishment = organization.Establishments[0]
      dispatch(establishmentStore.actions.setList(organization.Establishments))
      dispatch(establishmentStore.actions.setActive(establishment))
    }
    setIsDirty(false)
  }

  async function handleCreateOrgnanization() {
    const response: any = await dispatch(
      organizationStore.thunks.createEntity({
        ...createEmptyOrganization(),
        AccountId: _session.accountId,
        name: _session.accountName ?? "",
        isMultiple: false,
        Users: []
      })
    ).unwrap()

    //Force mode admin and establishment (mode Gaëlle)
    if (!response.error) {
      const establishment: Establishment = response.Establishments[0]
      dispatch(organizationStore.actions.setList([response]))
      dispatch(establishmentStore.actions.setList(response.Establishments))
      dispatch(establishmentStore.actions.setActive(establishment))
      editInterfaceType("ESTABLISHMENT", establishment.id)
      setIsDirty(true)
    }
  }

  function editInterfaceType(interfaceType: RISKS_INTERFACE_TYPE, establishmentId?: string) {
    store.dispatch(sessionEdit("risksInterfaceType", interfaceType))
    store.dispatch(sessionEdit("risksRole", interfaceType === "ORGANIZATION" ? "SUPERADMIN" : "ADMIN"))
    store.dispatch(sessionEdit("establishmentId", interfaceType === "ORGANIZATION" ? null : (establishmentId ?? draftOrganization.id)))
    setIsDirty(true)
  }

  const confirmDelete = async () => {
    if (!draftOrganization?.id) return
    await dispatch(organizationStore.thunks.removeEntity(draftOrganization.id)).unwrap()
    dispatch(organizationStore.actions.setActive(createEmptyOrganization()))
    dispatch(organizationStore.actions.cancel())
    setIsDirty(false)
    setIsDeleteModalOpen(false)
    navigate("/home")
  }

  return (
    <>
      <OrganizationDeleteModal
        isOpen={isDeleteModalOpen}
        name={draftOrganization.name}
        status={status}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />

      <PageHeader
        title={t("Organisation")}
        subtitle={
          <div className="flex items-center">
            {t("Renseignez les informations générales de votre organisation DUERP.")}
          </div>
        }
      />

      <Card>
        {organizationExists ? (
          <>
            <div className="flex gap-4 items-end">
              <TextInput
                type="text"
                title={t("Nom de l'organisation")}
                value={draftOrganization.name ?? draftOrganization.name ?? ""}
                onChange={updateField("name")}
                required
              />

              <TextInput
                title={t("N° SIREN")}
                type="text"
                value={draftOrganization.establishmentId ?? draftOrganization.establishmentId ?? ""}
                onChange={updateField("establishmentId")}
                required
              />

              {/* <div className="flex flex-col">
                <div className="text-sm opacity-0 select-none">.</div>
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox
                    active={Boolean(draftOrganization.isMultiple)}
                    onClick={(e) => {
                      editInterfaceType(e.value ? "ORGANIZATION" : "ESTABLISHMENT")
                      setValue("isMultiple", e.value)
                    }}
                  />
                  <span className="grey-t whitespace-nowrap">{t("Multiples établissements")}</span>
                  <Tooltip text={t("Votre organisation se compose de plusieurs établissements")} position="top">
                    <span className="chip-info _hover">
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </span>
                  </Tooltip>
                </div>
              </div> */}
            </div>

            <Space />

            <div className="flex mt-4">
              <Space />

              {organizationExists && (
                <Button onClick={handleDelete} icon={faTrash} isWithBorder>
                  {t("utils_delete")}
                </Button>
              )}

              <Button
                width={164}
                isLoading={status === "STATUS_SAVING" ? true : false}
                onClick={isDirty ? handleSave : undefined}
                icon={faSave}
                className={isDirty && draftOrganization.name ? "primary" : "light"}
                isWithBorder>
                {t("utils_save")}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between">
            <div className="grey-t font-bold">{t("Aucune organisation")}</div>
            <Button onClick={handleCreateOrgnanization} icon={faCity} isWithBorder isFullWidth>
              {t("Créer une organisation")}
            </Button>
          </div>
        )}
      </Card>
    </>
  )
}
